// useNodeDetails.js

import { useCallback } from 'react';

import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { buildNodeSubtitlePatch } from 'utilities/NodeSubtitle';
import { buildUserSummary } from 'utilities/Users';
import {
  beginProvisionalCommitFromPatch,
  commitProvisional,
} from 'node/provisional/provisionalNodeSession';
import { removeNodeCover, saveNodeCover } from 'services/nodes/NodeCoverService';
import { updateNode as updateNodeDoc } from 'services/nodes/NodeWriteService';

import { toast } from 'react-toastify';

export default function useNodeDetails({
  node,
  localNode,
  setLocalNode,
  setCurrentNode,
  user,
}) {
  const applyLocalPatch = useCallback((patch) => {
    if (!node?.key) return;
    setLocalNode((prev) => {
      if (prev?.key === node.key) return { ...prev, ...patch };
      // Seed from the live node so the first optimistic edit (e.g. cover remove) sticks.
      return { ...node, ...patch };
    });
    if (typeof setCurrentNode === 'function') {
      setCurrentNode((prev) => {
        if (prev?.key === node.key) return { ...prev, ...patch };
        return { ...node, ...patch };
      });
    }
  }, [node, setCurrentNode, setLocalNode]);

  const buildActorAuditPatch = useCallback(() => {
    const actorKey = String(user?.key || user?.userKey || user?.uid || '').trim();
    if (!actorKey) return {};

    return {
      updatedBy: actorKey,
      updatedByUser: buildUserSummary(user),
    };
  }, [user]);

  const persistPatch = useCallback((patch, errorMessage) => {
    if (!node?.key) return Promise.resolve(false);
    const nodeKey = node.key;
    const nodeBeforePatch = node;

    // Arm commit before the awaited write so peek close cannot discard mid-flight.
    const shouldClearProvisional = beginProvisionalCommitFromPatch(
      nodeKey,
      patch,
      nodeBeforePatch,
    );
    const persistedPatch = {
      ...patch,
      ...buildActorAuditPatch(),
      ...(shouldClearProvisional ? { provisional: false } : {}),
    };

    if (shouldClearProvisional) {
      applyLocalPatch({ provisional: false });
    }

    return updateNodeDoc(nodeKey, persistedPatch)
      .then(() => true)
      .catch((error) => {
        toast.error(`${errorMessage}: ${error.message || 'Unknown error'}`, {
          autoClose: 4000,
        });

        return false;
      });
  }, [applyLocalPatch, buildActorAuditPatch, node]);

  const handleTitleSaved = useCallback((title) => {
    if (!node?.key) return;
    const patch = { title };
    applyLocalPatch(patch);
    persistPatch(patch, 'Failed to update title');
  }, [node, applyLocalPatch, persistPatch]);

  const handleTagsSaved = useCallback((tags) => {
    if (!node?.key) return;
    const patch = { tags };
    applyLocalPatch(patch);
    persistPatch(patch, 'Failed to update tags');
  }, [node, applyLocalPatch, persistPatch]);

  const handleLocationSaved = useCallback((field, nextLocation) => {
    if (!node?.key || !field) return;
    const patch = buildNodeFieldPatch(node, field, node?.type, nextLocation ?? null);
    if (!patch) return;
    applyLocalPatch(patch);
    persistPatch(patch, 'Failed to update location');
  }, [node, applyLocalPatch, persistPatch]);

  const handleSubtitleSaved = useCallback((subtitle) => {
    if (!node?.key) return;
    const patch = buildNodeSubtitlePatch(subtitle);
    applyLocalPatch(patch);
    persistPatch(patch, 'Failed to update subtitle');
  }, [node?.key, applyLocalPatch, persistPatch]);

  const handleCoverSaved = useCallback((nodeKeyToUpdate, payload) => {
    if (!nodeKeyToUpdate || nodeKeyToUpdate !== node?.key) return;

    if (payload?.removed) {
      const patch = { coverUrl: null, coverStoragePath: null };
      const previousCover = {
        coverUrl: node?.coverUrl ?? null,
        coverStoragePath: node?.coverStoragePath ?? null,
      };
      const normalizedBaseKey = String(node?.baseKey || '').trim();
      const nodeBeforePatch = node;
      const shouldClearProvisional = beginProvisionalCommitFromPatch(
        nodeKeyToUpdate,
        patch,
        nodeBeforePatch,
      );

      // Hide immediately; persist in the background.
      applyLocalPatch(
        shouldClearProvisional ? { ...patch, provisional: false } : patch,
      );

      (async () => {
        try {
          await removeNodeCover({
            nodeKey: nodeKeyToUpdate,
            baseKey: normalizedBaseKey,
          });
          if (shouldClearProvisional) {
            await commitProvisional(nodeKeyToUpdate);
          }
        } catch (error) {
          applyLocalPatch(previousCover);
          toast.error(`Failed to remove cover: ${error.message || 'Unknown error'}`, {
            autoClose: 4000,
          });
        }
      })();

      return;
    }

    if (payload?.objectURL) {
      applyLocalPatch({ coverUrl: payload.objectURL });
    }

    if (payload?.blob instanceof Blob) {
      const blob = payload.blob;
      const normalizedBaseKey = String(node?.baseKey || '').trim();
      const nodeBeforePatch = node;
      // Arm before upload so peek close during the upload cannot discard.
      const intentPatch = {
        coverUrl: payload.objectURL || '__cover_pending__',
      };
      const shouldClearProvisional = beginProvisionalCommitFromPatch(
        nodeKeyToUpdate,
        intentPatch,
        nodeBeforePatch,
      );
      if (shouldClearProvisional) {
        applyLocalPatch({ provisional: false });
      }

      (async () => {
        try {
          const result = await saveNodeCover({
            file: blob,
            nodeKey: nodeKeyToUpdate,
            baseKey: normalizedBaseKey,
          });
          const storagePath = String(result?.storagePath || '').trim();
          const patch = {
            coverUrl: storagePath || null,
            coverStoragePath: storagePath || null,
          };
          applyLocalPatch(patch);
          if (shouldClearProvisional) {
            await commitProvisional(nodeKeyToUpdate);
          }
        } catch (error) {
          toast.error(`Failed to upload cover: ${error.message || 'Unknown error'}`, {
            autoClose: 4000,
          });
        }
      })();
    }
  }, [applyLocalPatch, node]);

  const handleNodeChange = useCallback((nodeKeyToUpdate, changes) => {
    if (!nodeKeyToUpdate || !changes) return;

    applyLocalPatch(changes);
    return persistPatch(changes, 'Failed to update');
  }, [applyLocalPatch, persistPatch, node]);

  return {
    handleTitleSaved,
    handleTagsSaved,
    handleLocationSaved,
    handleSubtitleSaved,
    handleCoverSaved,
    handleNodeChange,
  };
}
